import {
  activatePhaseProject,
  setMatterPhaseTeam,
  setMatterPhaseAssignment,
  retrievePhaseTeamOptions,
  retrievePhaseRoleOptions,
  retrievePhaseTeamMembers,
  applyMatterPhaseProject,
  archivePhaseProject,
  archiveReusablePhase,
  createPhaseProject,
  createManualPhaseAction,
  createReusablePhase,
  clearMatterPhaseActionManualStatus,
  clearMatterPhaseManualStatus,
  deletePhaseProject,
  deleteReusablePhase,
  reactivatePhaseProject,
  retroactivelyApplyPhaseProject,
  removeMatterPhaseProject,
  retrieveAllPhaseActionDefinitions,
  retrieveAllReusablePhases,
  retrieveMatterPhases,
  retrieveMatterPhaseApplicationOption,
  retrievePhaseActionDefinitions,
  retrievePhaseProject,
  retrievePhaseProjects,
  retrieveReusablePhases,
  retryMatterPhaseAction,
  setMatterPhaseActionManualStatus,
  setMatterPhaseActionCompletion,
  setMatterPhaseManualStatus,
  type MatterPhaseListParams,
  type PhaseProjectListParams,
  type ReusablePhaseListParams,
  updatePhaseProject,
  updateReusablePhase,
} from "@/lib/data/requests/matters/matter-phase";
import { queryKeys } from "@/lib/data/static/query-keys";
import type {
  APIError,
  APIResponse,
  MatterPhaseApplication,
} from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";
import toast from "react-hot-toast";

export const phaseProjectsQueryOptions = (
  params: PhaseProjectListParams = {},
) =>
  queryOptions({
    queryKey: [queryKeys.phaseProjects, params],
    queryFn: () => retrievePhaseProjects(params),
    placeholderData: keepPreviousData,
  });

export const phaseProjectQueryOptions = (projectId: string, enabled = true) =>
  queryOptions({
    queryKey: [queryKeys.phaseProjects, projectId],
    queryFn: () => retrievePhaseProject(projectId),
    enabled: enabled && Boolean(projectId),
  });

export const reusablePhasesQueryOptions = (
  params: ReusablePhaseListParams = {},
) =>
  queryOptions({
    queryKey: [queryKeys.reusableMatterPhases, params],
    queryFn: () => retrieveReusablePhases(params),
    placeholderData: keepPreviousData,
  });

export const phaseActionDefinitionsQueryOptions = (
  params: MatterPhaseListParams = {},
) =>
  queryOptions({
    queryKey: [queryKeys.phaseActionDefinitions, params],
    queryFn: () => retrievePhaseActionDefinitions(params),
    placeholderData: keepPreviousData,
  });

export const reusablePhasesCatalogQueryOptions = () =>
  queryOptions({
    queryKey: [queryKeys.reusableMatterPhases, "complete-catalog"],
    queryFn: () => retrieveAllReusablePhases(),
  });

export const phaseActionDefinitionsCatalogQueryOptions = () =>
  queryOptions({
    queryKey: [queryKeys.phaseActionDefinitions, "complete-catalog"],
    queryFn: () => retrieveAllPhaseActionDefinitions(),
  });

export const matterPhasesQueryOptions = (matterId: string, enabled = true) =>
  queryOptions({
    queryKey: [queryKeys.matterPhases, matterId],
    queryFn: () => retrieveMatterPhases(matterId),
    enabled: enabled && Boolean(matterId),
    refetchOnWindowFocus: true,
  });

export const matterPhaseApplicationOptionQueryOptions = (
  matterId: string,
  enabled = true,
) =>
  queryOptions({
    queryKey: [queryKeys.phaseProjects, "application-option", matterId],
    queryFn: () => retrieveMatterPhaseApplicationOption(matterId),
    enabled: enabled && Boolean(matterId),
    refetchOnWindowFocus: true,
  });

const useMatterPhaseMutation = <TVariables, TData>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  successMessage: string,
  fallbackErrorMessage: string,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, fallbackErrorMessage);
    },
    onSuccess: () => {
      toast.success(successMessage);
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [queryKeys.phaseProjects],
        }),
        queryClient.invalidateQueries({
          queryKey: [queryKeys.reusableMatterPhases],
        }),
        queryClient.invalidateQueries({
          queryKey: [queryKeys.phaseActionDefinitions],
        }),
      ]);
    },
  });
};

export const useCreatePhaseProject = () =>
  useMatterPhaseMutation(
    createPhaseProject,
    "Phase Project created successfully",
    "Error creating Phase Project",
  );

export const useUpdatePhaseProject = () =>
  useMatterPhaseMutation(
    updatePhaseProject,
    "Phase Project saved successfully",
    "Error saving Phase Project",
  );

export const useRetroactivelyApplyPhaseProject = () =>
  useMutation({
    mutationFn: retroactivelyApplyPhaseProject,
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(
        error,
        "Error applying Phase Project changes to existing Matters",
      );
    },
    onSuccess: (response) => {
      const count = response.data.targetMatterCount;
      toast.success(
        count === 1
          ? "Phase Project update queued for 1 Matter"
          : `Phase Project update queued for ${count} Matters`,
      );
    },
  });

export const useActivatePhaseProject = () =>
  useMatterPhaseMutation(
    activatePhaseProject,
    "Phase Project activated successfully",
    "Error activating Phase Project",
  );

export const useArchivePhaseProject = () =>
  useMatterPhaseMutation(
    archivePhaseProject,
    "Phase Project archived successfully",
    "Error archiving Phase Project",
  );

export const useReactivatePhaseProject = () =>
  useMatterPhaseMutation(
    reactivatePhaseProject,
    "Phase Project reactivated successfully",
    "Error reactivating Phase Project",
  );

export const useDeletePhaseProject = () =>
  useMatterPhaseMutation(
    deletePhaseProject,
    "Phase Project deleted successfully",
    "Error deleting Phase Project",
  );

export const useCreateReusablePhase = () =>
  useMatterPhaseMutation(
    createReusablePhase,
    "Reusable Phase created successfully",
    "Error creating Reusable Phase",
  );

export const useCreateManualPhaseAction = () =>
  useMatterPhaseMutation(
    createManualPhaseAction,
    "Checkbox Action created successfully",
    "Error creating checkbox Action",
  );

export const useUpdateReusablePhase = () =>
  useMatterPhaseMutation(
    updateReusablePhase,
    "Reusable Phase saved successfully",
    "Error saving Reusable Phase",
  );

export const useArchiveReusablePhase = () =>
  useMatterPhaseMutation(
    archiveReusablePhase,
    "Reusable Phase archived successfully",
    "Error archiving Reusable Phase",
  );

export const useDeleteReusablePhase = () =>
  useMatterPhaseMutation(
    deleteReusablePhase,
    "Reusable Phase deleted successfully",
    "Error deleting Reusable Phase",
  );

const useMatterPhaseApplicationMutation = <
  TVariables extends { matterId: string },
>(
  mutationFn: (
    variables: TVariables,
  ) => Promise<APIResponse<MatterPhaseApplication | null>>,
  successMessage: string,
  fallbackErrorMessage: string,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, fallbackErrorMessage);
    },
    onSuccess: (response, variables) => {
      queryClient.setQueryData<APIResponse<MatterPhaseApplication | null>>(
        [queryKeys.matterPhases, variables.matterId],
        (current) => ({
          ...response,
          pagination: current?.pagination,
        }),
      );
      toast.success(successMessage);
    },
    onSettled: async (_data, _error, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [queryKeys.matters, variables.matterId],
        }),
        queryClient.invalidateQueries({
          queryKey: [queryKeys.matters, "user-tasks"],
        }),
        queryClient.invalidateQueries({
          queryKey: [queryKeys.matterPhases, variables.matterId],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            queryKeys.phaseProjects,
            "application-option",
            variables.matterId,
          ],
        }),
      ]);
    },
  });
};

export const useApplyMatterPhaseProject = () =>
  useMatterPhaseApplicationMutation(
    applyMatterPhaseProject,
    "Phase Project applied successfully",
    "Error applying Phase Project",
  );

export const useRemoveMatterPhaseProject = () =>
  useMatterPhaseApplicationMutation(
    removeMatterPhaseProject,
    "Phase Project removed successfully",
    "Error removing Phase Project",
  );

export const useSetMatterPhaseManualStatus = () =>
  useMatterPhaseApplicationMutation(
    setMatterPhaseManualStatus,
    "Phase status updated successfully",
    "Error updating Phase status",
  );

export const useClearMatterPhaseManualStatus = () =>
  useMatterPhaseApplicationMutation(
    clearMatterPhaseManualStatus,
    "Phase status cleared successfully",
    "Error clearing Phase status",
  );

export const useSetMatterPhaseActionManualStatus = () =>
  useMatterPhaseApplicationMutation(
    setMatterPhaseActionManualStatus,
    "Action status updated successfully",
    "Error updating Action status",
  );

export const useSetMatterPhaseActionCompletion = () =>
  useMatterPhaseApplicationMutation(
    setMatterPhaseActionCompletion,
    "Phase action updated",
    "Error updating phase action",
  );

export const useClearMatterPhaseActionManualStatus = () =>
  useMatterPhaseApplicationMutation(
    clearMatterPhaseActionManualStatus,
    "Action status cleared successfully",
    "Error clearing Action status",
  );

export const useRetryMatterPhaseAction = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: retryMatterPhaseAction,
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error retrying Action evaluation");
    },
    onSuccess: () => {
      toast.success("Action evaluation requested");
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matterPhases, variables.matterId],
      });
    },
  });
};

export const useSetMatterPhaseTeam = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setMatterPhaseTeam,
    onSuccess: () => toast.success("Phase team updated"),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Unable to update phase team");
    },
    onSettled: async (_, _error, { matterId }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [queryKeys.matterPhases, matterId],
        }),
        queryClient.invalidateQueries({
          queryKey: [queryKeys.matters, matterId],
        }),
        queryClient.invalidateQueries({
          queryKey: [queryKeys.matterDashboardRows],
        }),
        queryClient.invalidateQueries({
          queryKey: [queryKeys.matterDashboardUnassignedActions],
        }),
      ]);
    },
  });
};

export const phaseTeamOptions = (query: string) =>
  queryOptions({
    queryKey: [queryKeys.matterPhases, "team-options", query],
    queryFn: ({ signal }) => retrievePhaseTeamOptions(query, signal),
    staleTime: 30_000,
  });
export const phaseRoleOptions = (query: string) =>
  queryOptions({
    queryKey: [queryKeys.matterPhases, "role-options", query],
    queryFn: ({ signal }) => retrievePhaseRoleOptions(query, signal),
    staleTime: 30_000,
  });
export const phaseTeamMembersOptions = (teamId: string) =>
  queryOptions({
    queryKey: [queryKeys.matterPhases, "team-members", teamId],
    queryFn: ({ signal }) => retrievePhaseTeamMembers(teamId, signal),
  });
export const useSetMatterPhaseAssignment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setMatterPhaseAssignment,
    onSuccess: (response, { matterId }) => {
      queryClient.setQueryData<APIResponse<MatterPhaseApplication | null>>(
        [queryKeys.matterPhases, matterId],
        (current) => ({ ...response, pagination: current?.pagination }),
      );
      toast.success("Phase assignment updated");
    },
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Unable to update phase assignment");
    },
    onSettled: (_, _error, { matterId }) => {
      // The canonical response enables the next edit while related views refresh.
      void Promise.all([
        queryClient.invalidateQueries({
          queryKey: [queryKeys.matterPhases, matterId],
        }),
        queryClient.invalidateQueries({ queryKey: [queryKeys.matters] }),
        queryClient.invalidateQueries({
          queryKey: [queryKeys.matterDashboardRows],
        }),
        queryClient.invalidateQueries({
          queryKey: [queryKeys.matterDashboardUnassignedActions],
        }),
      ]);
    },
  });
};
